import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { NavigationService } from '../services/navigation.service';
import { ApiService } from '../services/api.service';

/**
 * Permission Guard
 * 
 * Checks if the user has permission to access a specific route based on their role and permissions.
 * If the user doesn't have access, redirects them to the 404 page.
 * 
 * This guard should be used after authGuard to ensure the user is authenticated.
 */
@Injectable({
  providedIn: 'root'
})
export class PermissionGuard implements CanActivate {
  private navigationService = inject(NavigationService);
  private apiService = inject(ApiService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const url = state.url;
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    if (
      url.includes('/signup') ||
      currentPath.includes('/signup') ||
      url.includes('/no-organization') ||
      url.includes('/no-parking') ||
      url.includes('/not-found')
    ) {
      return true;
    }

    // Ensure session context (POV/ID) is synced with the current URL before checking permissions
    this.navigationService.handlePageRefresh(url);

    // Extract base route path (remove query parameters and normalize)
    const baseRoute = this.extractBaseRoute(url);

    // Check if user is authenticated (should already be checked by authGuard, but double-check)
    const currentUser = this.apiService.getCurrentUser();
    if (!currentUser) {
      this.router.navigate(['/login']);
      return false;
    }

    // Check if user has access to this route
    const hasAccess = this.navigationService.hasAccess(baseRoute);

    if (!hasAccess) {
      // Redirect to 404 page while preserving original requested URL in address bar
      this.router.navigate(['/not-found'], { skipLocationChange: true });
      return false;
    }

    return true;
  }

  /**
   * Extract base route from URL, removing parameters and query strings
   * Examples:
   *   /dashboard/123 -> /dashboard
   *   /parkings/create/step1 -> /parkings/create/step1
   *   /settings/integrations/456 -> /settings/integrations
   *   /dashboard/orgId/parkingId/parkingName -> /dashboard
   */
  private extractBaseRoute(url: string): string {
    // Remove query parameters
    const pathOnly = url.split('?')[0];

    // Split into segments
    const segments = pathOnly.split('/').filter(segment => segment.length > 0);

    if (segments.length === 0) {
      return '/dashboard';
    }

    // Handle dynamic user_pov routes: /:pov/:orgId/:pageName (e.g., /organization/:orgId/dashboard)
    // Check if first segment is not a known route and second segment is a UUID
    const knownRoutes = ['login', 'signup', 'dashboard', 'settings', 'parkings', 'parking-lots', 'customers', 'reports', 'report', 'session', 'passes', 'facilities', 'reservations', 'team', 'billing', 'subscriptions', 'plans', 'transactions', 'organisations', 'admin-dashboard', 'live-dashboard', 'ai', 'sistem-ai-insight', 'vehicle-sessions', 'linked-vehicles', 'data-exports', 'data-export', 'data-import', 'dataimport', 'facility-usage-logs', 'customer', 'reservation', 'parking', 'parking-add-people', 'parking-add-capacity', 'review-parking', 'subscriptions-plans', 'parking-profile', 'profile', 'upgrade-plan', 'billing-invoice', 'payment-gateway', 'payment-options', 'taxes', 'organization-profile', 'org-profile', 'media-branding', 'passes-details', 'report-view', 'admin-full-report', 'facility-activities', 'setup-guide', 'timezone-settings', 'timezone-demo', 'global-settings', 'role-navigation-demo', 'role-switcher-debug', 'role-switcher-test', 'not-found', 'network-error', 'pos'];

    if (segments.length >= 2) {
      const firstSegment = segments[0];
      const secondSegment = segments[1];
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

      // If first segment is not a known route and second segment is a UUID, it's likely a dynamic pov route
      if (!knownRoutes.includes(firstSegment) && uuidPattern.test(secondSegment) && segments.length >= 3) {
        // Handle nested routes like /:pov/:orgId/settings/:subRoute
        if (segments.length >= 4 && segments[2] === 'settings') {
          // Return /:pov/:orgId/settings/:subRoute (e.g., /organization/123/settings/org-profile)
          return `/${segments[0]}/${segments[1]}/${segments[2]}/${segments[3]}`;
        }
        // Return /:pov/:orgId/:pageName (e.g., /organization/123/dashboard)
        return `/${segments[0]}/${segments[1]}/${segments[2]}`;
      }
    }

    // Handle /parking/:parkingId/:pageName routes - preserve parking routes
    if (segments.length >= 2 && segments[0] === 'parking') {
      const parkingId = segments[1];
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

      // If second segment is a UUID (parkingId), preserve the route structure
      if (uuidPattern.test(parkingId) && segments.length >= 3) {
        // Handle nested routes like /parking/:parkingId/settings/:subRoute
        if (segments.length >= 4 && segments[2] === 'settings') {
          // Return /parking/:parkingId/settings/:subRoute (e.g., /parking/123/settings/org-profile)
          return `/${segments[0]}/${segments[1]}/${segments[2]}/${segments[3]}`;
        }
        // Return /parking/:parkingId/:pageName (e.g., /parking/123/vehicle-sessions)
        return `/${segments[0]}/${segments[1]}/${segments[2]}`;
      }
    }

    // Handle /org/:orgId/:pageName routes - preserve organization routes (backward compatibility)
    if (segments.length >= 2 && segments[0] === 'org') {
      const orgId = segments[1];
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

      // If second segment is a UUID (orgId), preserve the route structure
      if (uuidPattern.test(orgId) && segments.length >= 3) {
        // Handle nested routes like /org/:orgId/settings/:subRoute
        if (segments.length >= 4 && segments[2] === 'settings') {
          // Return /org/:orgId/settings/:subRoute (e.g., /org/123/settings/org-profile)
          return `/${segments[0]}/${segments[1]}/${segments[2]}/${segments[3]}`;
        }
        // Return /org/:orgId/:pageName (e.g., /org/123/dashboard)
        return `/${segments[0]}/${segments[1]}/${segments[2]}`;
      }
    }

    // Handle special routes that should preserve their structure
    const specialRoutes = [
      'parkings/create',
      'settings'
    ];

    // Handle special routes that should preserve their structure
    // Check if route starts with a special route
    for (const specialRoute of specialRoutes) {
      const specialSegments = specialRoute.split('/');
      if (segments.length >= specialSegments.length) {
        const matches = specialSegments.every((seg, index) => segments[index] === seg);
        if (matches) {
          // For settings routes, preserve the sub-route (remove any parameters after)
          if (specialRoute === 'settings' && segments.length > 1) {
            // Check if second segment is a UUID or numeric ID (parameter)
            const secondSegment = segments[1];
            const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
            const isNumeric = /^\d+$/.test(secondSegment);

            // If second segment is a parameter, return just /settings
            if (uuidPattern.test(secondSegment) || isNumeric) {
              return `/${segments[0]}`;
            }

            // Otherwise return /settings/submodule (e.g., /settings/integrations)
            // If there are more segments (parameters), ignore them
            return `/${segments[0]}/${segments[1]}`;
          }
          // For parkings/create, preserve the full path up to the step
          if (specialRoute === 'parkings/create' && segments.length >= 3) {
            // Check if third segment is a step name (not a UUID)
            const thirdSegment = segments[2];
            const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(thirdSegment);
            if (!isUuid && (thirdSegment.startsWith('step') || ['parking-type', 'location-address', 'complete-details', 'parking-details', 'amenities', 'working-hours', 'assign-manager', 'review'].includes(thirdSegment))) {
              return `/${segments[0]}/${segments[1]}/${segments[2]}`;
            }
          }
          return `/${specialRoute}`;
        }
      }
    }

    // For regular routes, check if segments contain UUIDs (which are parameters)
    // UUID pattern: 8-4-4-4-12 hex characters
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    // Find the first UUID in the segments
    let firstUuidIndex = -1;
    for (let i = 0; i < segments.length; i++) {
      if (uuidPattern.test(segments[i])) {
        firstUuidIndex = i;
        break;
      }
    }

    // If UUID found, return route up to (but not including) the UUID
    if (firstUuidIndex > 0) {
      return '/' + segments.slice(0, firstUuidIndex).join('/');
    }

    // Check for numeric IDs (simple numbers)
    // If last segment is numeric and not a known route name, treat it as parameter
    const lastSegment = segments[segments.length - 1];
    const isNumeric = /^\d+$/.test(lastSegment);
    const knownRouteNames = ['dashboard', 'parkings', 'parking-lots', 'customers', 'reports', 'report', 'session', 'passes', 'facilities', 'reservations', 'settings', 'team', 'billing', 'subscriptions', 'plans', 'transactions', 'organisations', 'admin-dashboard', 'live-dashboard', 'ai', 'sistem-ai-insight', 'vehicle-sessions', 'linked-vehicles', 'data-exports', 'data-export', 'data-import', 'dataimport', 'facility-usage-logs', 'customer', 'reservation', 'parking', 'parking-add-people', 'parking-add-capacity', 'review-parking', 'subscriptions-plans', 'parking-profile', 'profile', 'upgrade-plan', 'billing-invoice', 'payment-gateway', 'payment-options', 'taxes', 'organization-profile', 'org-profile', 'media-branding', 'passes-details', 'report-view', 'admin-full-report', 'facility-activities', 'setup-guide', 'timezone-settings', 'timezone-demo', 'global-settings', 'role-navigation-demo', 'role-switcher-debug', 'role-switcher-test', 'pos'];

    if (isNumeric && segments.length > 1 && !knownRouteNames.includes(lastSegment)) {
      // Last segment is numeric ID, return route without it
      return '/' + segments.slice(0, segments.length - 1).join('/');
    }

    // Check for routes with multiple parameters (e.g., /dashboard/orgId/parkingId/parkingName)
    // If we have 3+ segments and the last two look like UUIDs or the pattern matches vendor view
    if (segments.length >= 3) {
      const secondLast = segments[segments.length - 2];
      const last = segments[segments.length - 1];

      // If second last is UUID and last is not UUID (parking name), remove both
      if (uuidPattern.test(secondLast) && !uuidPattern.test(last)) {
        return '/' + segments.slice(0, segments.length - 2).join('/');
      }

      // If both are UUIDs (orgId/parkingId), remove both
      if (uuidPattern.test(secondLast) && uuidPattern.test(last)) {
        return '/' + segments.slice(0, segments.length - 2).join('/');
      }
    }

    // If no parameters detected, return the full path
    return '/' + segments.join('/');
  }
}

